import {
  CSV_MAX_FILE_SIZE_MB,
  FILE_EXTENSIONS,
  IMPORT_SUPPORTED_FORMATS
} from "Constants";
import { LoadingIndicator } from "elements";
import { useTenantId } from "polyrouter/SelectedTenantProvider";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { State } from "store";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { Schema } from "yup";
import { FormikValues } from "formik/dist/types";
import { FileUpload } from "./FileUpload";
import { useImportActions } from "store/import";
import { DISMISS, useNotification } from "collections";
import {
  ImportResponse,
  Maybe,
  useGetImportReportQuery,
  useImportMutation
} from "generated/graphql";
import { useImportResponseHandler } from "./Migration";

export type ImportType =
  | "MIGRATION"
  | "DEVICE"
  | "DEVICE_GROUP"
  | "SITE"
  | "DEVICE_MODEL";

export const Import: React.FC<{
  importType: ImportType;
  fileTypesAccepted: FILE_EXTENSIONS[];
  validationSchema: Schema<FormikValues>;
  prepareFileForImport: (file: File, type: ImportType) => Promise<string>;
  importRules: React.ReactNode;
  templateDownload?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  pollInterval?: number;
}> = ({
  importType,
  fileTypesAccepted,
  validationSchema,
  prepareFileForImport,
  importRules,
  templateDownload = null,
  loading = false,
  disabled = false,
  disabledReason,
  pollInterval = 5000
}) => {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const {
    createImportResponseText,
    handleDeviceImportResponse,
    handleSiteTopologyResponse,
    handleSiteConfigResponse,
    handleGroupConfigResponse,
    handleGlobalConfigResponse,
    buildErrorMessage
  } = useImportResponseHandler();
  const tenantId = useTenantId();
  const [importReport, setImportReport] = useState<Maybe<ImportResponse>>();
  const [importSuccess, setImportSuccess] = useState(false);
  const [errorContent, setErrorContent] = useState<React.ReactNode>(null);
  const [{ fetching: initiating }, initiateImport] = useImportMutation();

  const [startImport, updateProgress, resetImport] = useImportActions();
  const { inProgress, progress, finished, files } = useSelector(
    (state: State) => {
      const importState = state.import[tenantId];
      return importState && importState.type === importType
        ? importState
        : {
            inProgress: false,
            progress: 0.0,
            finished: false,
            files: []
          };
    }
  );
  const anotherImportState = useSelector((state: State) => {
    const importState = state.import[tenantId];
    return importState?.type && importState.type !== importType
      ? importState
      : {
          inProgress: false,
          finished: false
        };
  });
  const anotherImportNotComplete =
    anotherImportState.inProgress || anotherImportState.finished;

  const successKey: LocalKeys = `${importType}_IMPORT_SUCCESS` as const;
  const successKeyLong: LocalKeys = `${importType}_IMPORT_SUCCESS_LONG` as const;
  const errorKey: LocalKeys = `${importType}_IMPORT_ERROR` as const;
  const errorTitleKey: LocalKeys = `${importType}_IMPORT_HEADER_ERROR` as const;
  const configTitleKey: LocalKeys = `${importType}_IMPORT_HEADER_SUCCESS` as const;

  const successMessage = t(successKey);
  const successMessageLong = t(successKeyLong);
  const errorMessage = t(errorKey);
  const errorSectionTitle = t(errorTitleKey);
  const configSectionTitle = t(configTitleKey);
  const reportFilename = `${importType.toLowerCase()}-errors.txt`;

  // periodically query for import report until import is finished
  const { data, executor: getImportReport } = useGetImportReportQuery({
    variables: {
      tenantId
    },
    pause: !inProgress,
    requestPolicy: "network-only",
    pollInterval: inProgress ? pollInterval : undefined
  });

  // once data arrives update import report
  useEffect(() => {
    if (data?.getImportReport) {
      setImportReport(data?.getImportReport);
    }
  }, [data?.getImportReport]);

  // once import is started, query for report once immediately to cover for cases where import will be quick
  // also, query for report if import is finished, but report is not available (this might happen if user leaves the import page and comes back later)
  useEffect(() => {
    if ((inProgress || finished) && !importReport) {
      getImportReport({ requestPolicy: "network-only" });
    }
  }, [inProgress, finished, importReport, getImportReport]);

  // update progress state
  useEffect(() => {
    if (inProgress && importReport?.progress != null) {
      const newProgress = parseFloat(importReport.progress);
      if (newProgress !== progress) {
        updateProgress({ tenantId, progress: newProgress });
      }
    }
  }, [tenantId, inProgress, importReport?.progress, progress, updateProgress]);

  const processImportReport = useCallback(
    (response: ImportResponse) => {
      let errorText = "";
      let configText = "";

      if (response?.errors?.length) {
        errorText = errorText.concat(response.errors.join("\n")) + "\n\n";
      }
      if (response?.deviceConfigResult?.length) {
        const deviceConfig = handleDeviceImportResponse(
          response.deviceConfigResult
        );
        if (deviceConfig.deviceErrors.length) {
          errorText = errorText.concat(
            deviceConfig.deviceErrors.toString() + "\n\n\n"
          );
        }
        if (deviceConfig.configParam.length) {
          configText = configText.concat(
            deviceConfig.configParam.toString() + "\n\n\n"
          );
        }
      }
      if (response?.siteTopologyResult?.length) {
        const siteTopology = handleSiteTopologyResponse(
          response.siteTopologyResult
        );
        if (siteTopology.length) {
          errorText = errorText.concat(siteTopology + "\n\n\n");
        }
      }
      if (response?.siteConfigResult?.length) {
        const siteConfig = handleSiteConfigResponse(response.siteConfigResult);
        if (siteConfig.errors.length) {
          errorText = errorText.concat(siteConfig.errors + "\n\n\n");
        }
        if (siteConfig.configParam.length) {
          configText = configText.concat(siteConfig.configParam + "\n\n\n");
        }
      }
      if (response?.groupConfigResult?.length) {
        const groupConfig = handleGroupConfigResponse(
          response.groupConfigResult
        );
        if (groupConfig.errors.length) {
          errorText = errorText.concat(groupConfig.errors + "\n\n\n");
        }
        if (groupConfig.configParam.length) {
          configText = configText.concat(groupConfig.configParam + "\n\n\n");
        }
      }
      if (response?.globalConfigResult?.length) {
        const globalConfig = handleGlobalConfigResponse(
          response.globalConfigResult
        );
        if (globalConfig.length) {
          configText = configText.concat(globalConfig);
        }
      }

      const importResponse = createImportResponseText(
        errorText,
        configText,
        errorSectionTitle,
        configSectionTitle
      );

      if (importResponse.length) {
        setImportSuccess(false);
        setErrorContent(
          buildErrorMessage(errorMessage, importResponse, reportFilename)
        );
      } else if (response?.success) {
        setImportSuccess(true);
        setErrorContent(null);
        addNotification({
          id: "import-success",
          type: "success",
          message: successMessage,
          dismiss: DISMISS.DEFAULT
        });
      } else {
        setImportSuccess(false);
        setErrorContent(null);
      }
    },
    // some dependencies are omitted, otherwise causes useEffect to call infinitely with no usefulness
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      configSectionTitle,
      errorSectionTitle,
      errorMessage,
      successMessage,
      reportFilename
    ]
  );

  const doImport = async (
    files: File[],
    updateExistingDevices?: boolean,
    overwriteDeviceRecords?: boolean
  ) => {
    let importData = "";

    const file = files[0];
    try {
      importData = await prepareFileForImport(file, importType);
    } catch (e) {
      console.log(e);
      return;
    }

    const { data, error } = await initiateImport(
      {
        tenantId: tenantId || "",
        fileName: file.name,
        file: importData,
        importType,
        updateExistingDevices,
        overwriteDeviceRecords
      },
      {
        notify: {
          message: errorMessage,
          dismiss: DISMISS.USER_ONLY
        }
      }
    );

    startImport({
      tenantId,
      type: importType,
      files: files.map(({ name, type }) => ({ name, type })),
      updateExistingDevices,
      overwriteDeviceRecords
    });

    if (!data?.importToLens || !!error) {
      // initiating import wasn't successful, set progress to 1.0 (100%) so import is marked as finished
      updateProgress({ tenantId, progress: 1.0 });
      setImportSuccess(false);
    }

    if (error?.response?.status === 413) {
      setErrorContent(<p className="error-text">{t("FILE_SIZE_ERROR")}</p>);
    }
  };

  const completeImport = () => {
    resetImport({ tenantId });
    setImportReport(undefined);
    setImportSuccess(false);
    setErrorContent(null);
  };

  // process response once import is finished
  useEffect(() => {
    if (finished && importReport) {
      processImportReport(importReport);
    }
  }, [finished, processImportReport, importReport]);

  return (
    <div className="p-1" data-testid="import-container">
      {importRules}
      {templateDownload}
      {loading ? (
        <LoadingIndicator className="inline" />
      ) : (
        <FileUpload
          disabled={disabled || anotherImportNotComplete}
          disabledReason={
            anotherImportNotComplete
              ? t("IMPORT_UNAVAILABLE_ANOTHER_IMPORT_IN_PROGRESS")
              : disabledReason
          }
          dragText={t("FILE_SIZE_ERROR_DESC", { size: CSV_MAX_FILE_SIZE_MB })}
          dropText={t("IMPORT_FILE_DRAG_DESC", {
            fileExtensions: fileTypesAccepted.join(` ${t("OR")} `)
          })}
          fileTypesAccepted={fileTypesAccepted
            .flatMap(type => IMPORT_SUPPORTED_FORMATS[type])
            .join(", ")}
          status={{
            inProgress: !!inProgress || initiating,
            progress: progress ?? 0,
            finished: !!finished,
            success: importSuccess,
            successMessage: successMessageLong,
            errorContent,
            files: files ?? []
          }}
          onSubmit={doImport}
          onDone={completeImport}
          validationSchema={validationSchema}
          importType={importType}
        />
      )}
    </div>
  );
};
